import React, { Fragment, useEffect } from 'react';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Panel, Loader, Placeholder, Message } from 'rsuite';
import { fetchVisitColumns } from '../../redux/sagas/visit/routines';
import { fetchClientColumns } from '../../redux/sagas/client/routines';
import { fetchOutreachAggregateOptions } from '../../redux/sagas/report/routines';
import { Status as DownloadRequestStatus } from '../../constants/download-request';
import OutreachAggregateTable from '../OutreachAggregateTable';
import SearchDetails, { ReadableQueryType } from './SearchDetails';
import Actions, { SearchQueryType } from './Actions';
import { CustomColumnLabel } from './constants';

const CustomMessage = styled(Message)`
  margin: 10px auto;
`;

function OutreachAggregateView(props) {
  const dispatch = useDispatch();
  const {
    result,
    downloadRequest,
    searchQuery,
    readableSearchQuery,
    isDataLoaded
  } = props;

  useEffect(
    () => {
      if (!isDataLoaded) {
        dispatch(fetchOutreachAggregateOptions());
        dispatch(fetchVisitColumns());
        dispatch(fetchClientColumns());
      }
    },
    [isDataLoaded]
  );

  const getCustomColumnWithSuffixLabel = col => {
    const { columnLabels } = props;
    const colArr = col.split('_');
    const column = [colArr[0], colArr[1], colArr[2]].join('_');
    const columnLabel = fp.get(column, columnLabels);
    const suffixLabel = fp.flow(
      fp.drop(3),
      fp.join('_'),
      fp.startCase
    )(colArr);
    return `${columnLabel} ${suffixLabel}`;
  };

  const dataColumns = () => {
    const primaryColumns = ['visit_count'];
    const columns = fp.flow(
      fp.flatMap(fp.keys),
      fp.uniq,
      fp.without(primaryColumns)
    )(result);

    return [...primaryColumns, ...columns.sort()];
  };

  const isCustomColumnWithSuffix = column =>
    /^cf_/.test(column) && column.split('_').length > 3;

  const columnsWithLabel = () => {
    const { columnLabels } = props;
    const labelByColumn = { ...columnLabels, ...CustomColumnLabel };
    return fp.map(
      column => ({
        column,
        label: isCustomColumnWithSuffix(column)
          ? getCustomColumnWithSuffixLabel(column)
          : fp.getOr(fp.startCase(column), column, labelByColumn)
      }),
      dataColumns()
    );
  };
  const reportName = fp.get('name', downloadRequest);
  const isInProgress =
    DownloadRequestStatus.IN_PROGRESS === fp.get('status', downloadRequest);
  const showContent =
    !fp.isEmpty(searchQuery) && !fp.isEmpty(readableSearchQuery);
  const hasResult = !fp.isEmpty(result);

  return (
    <Fragment>
      {!isDataLoaded && <Loader center />}
      {showContent && (
        <Panel bodyFill>
          {hasResult && (
            <Actions
              searchQuery={searchQuery}
              searchDetails={readableSearchQuery}
              columnsWithLabel={columnsWithLabel()}
              reportName={reportName}
              result={result}
            />
          )}
          <SearchDetails
            searchDetails={readableSearchQuery}
            reportName={reportName}
          />
          {isInProgress && (
            <Fragment>
              <CustomMessage
                align="center"
                description="Your report is being generated it can take some time please wait for the page to show results."
              />
              <Placeholder.Grid rows={3} columns={5} />
            </Fragment>
          )}
          {(!isInProgress || hasResult) && (
            <OutreachAggregateTable
              columns={columnsWithLabel()}
              loading={false}
              data={result}
            />
          )}
        </Panel>
      )}
    </Fragment>
  );
}

OutreachAggregateView.propTypes = {
  result: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  downloadRequest: PropTypes.shape({}).isRequired,
  searchQuery: SearchQueryType.isRequired,
  isDataLoaded: PropTypes.bool.isRequired,
  readableSearchQuery: ReadableQueryType,
  columnLabels: PropTypes.shape({})
};

OutreachAggregateView.defaultProps = {
  columnLabels: {},
  readableSearchQuery: {}
};

export default OutreachAggregateView;
